/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Star, Send, AlertCircle, CheckCircle2, MessageSquare } from 'lucide-react';
import { cn } from '../../lib/utils';

// Rating criteria definitions
const CRITERIA = [
    { id: 'suivi', label: 'Suivi', description: 'Qualité du suivi et de la communication régulière' },
    { id: 'clarte', label: 'Clarté', description: 'Clarté des explications et transparence' },
    { id: 'professionnalisme', label: 'Professionnalisme', description: 'Professionnalisme et respect des engagements' }
];

function StarRating({ value, onChange, disabled }) {
    const [hover, setHover] = useState(0);

    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={disabled}
                    className={cn(
                        'p-1 transition-transform',
                        !disabled && 'hover:scale-110 cursor-pointer',
                        disabled && 'cursor-not-allowed opacity-60'
                    )}
                    onMouseEnter={() => !disabled && setHover(star)}
                    onMouseLeave={() => !disabled && setHover(0)}
                    onClick={() => !disabled && onChange(star)}
                >
                    <Star
                        className={cn(
                            'w-8 h-8 transition-colors',
                            (hover || value) >= star 
                                ? 'fill-yellow-400 text-yellow-400' 
                                : 'text-gray-300'
                        )}
                    />
                </button>
            ))}
        </div>
    );
}

export default function RatingPage() {
    const { ratingId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [rating, setRating] = useState(null);
    const [mandate, setMandate] = useState(null);
    const [agent, setAgent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [criteria, setCriteria] = useState({});
    const [comment, setComment] = useState('');

    useEffect(() => {
        loadRating();
    }, [ratingId]);

    const loadRating = async () => {
        try {
            // Get pending ratings to find this one
            const pendingRes = await api.get('/ratings/my-pending');
            const foundRating = pendingRes.data.ratings?.find(r => r.id === ratingId);
            
            if (!foundRating) {
                toast.error('Avis non trouvé ou déjà soumis');
                navigate('/seller');
                return;
            }

            setRating(foundRating);
            
            // Load mandate details
            const mandateRes = await api.get(`/mandates/${foundRating.mandate_id}`);
            setMandate(mandateRes.data);

            // Load agent details
            const agentRes = await api.get(`/users/${foundRating.agent_id}`);
            setAgent(agentRes.data);

            // Pre-fill criteria if draft
            if (foundRating.criteria && Object.keys(foundRating.criteria).length > 0) {
                setCriteria(foundRating.criteria);
            }
            if (foundRating.comment) {
                setComment(foundRating.comment);
            }
        } catch (error) {
            console.error('Error loading rating:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        // Validate all criteria are filled
        for (const c of CRITERIA) {
            if (!criteria[c.id] || criteria[c.id] < 1) {
                toast.error(`Veuillez noter le critère "${c.label}"`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const res = await api.post(`/ratings/${ratingId}/submit`, {
                criteria,
                comment: comment.trim() || null
            });

            if (res.data.status === 'CONFIRMED') {
                toast.success('Merci ! Votre avis a été publié.');
            } else {
                toast.success('Votre avis a été envoyé. L\'agent peut y répondre.');
            }

            navigate('/seller');
        } catch (error) {
            console.error('Error submitting rating:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSubmitting(false);
        }
    };

    const overallScore = Object.values(criteria).length === CRITERIA.length
        ? (Object.values(criteria).reduce((a, b) => a + b, 0) / CRITERIA.length).toFixed(1)
        : null;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner w-8 h-8"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#681793]/5 to-[#FF914D]/5 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold mb-2">Donnez votre avis</h1>
                    <p className="text-muted-foreground">
                        Aidez les futurs vendeurs en partageant votre expérience
                    </p>
                </div>

                {/* Agent Card */}
                {agent && (
                    <Card className="mb-6">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white text-xl font-bold">
                                    {agent.first_name?.charAt(0)}{agent.last_name?.charAt(0)}
                                </div>
                                <div>
                                    <p className="font-semibold text-lg">
                                        {agent.first_name} {agent.last_name}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Mandat n°{mandate?.mandate_number}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Rating Form */}
                <Card>
                    <CardHeader>
                        <CardTitle>Évaluez votre expérience</CardTitle>
                        <CardDescription>
                            Notez chaque critère de 1 à 5 étoiles
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Criteria */}
                        {CRITERIA.map((c) => (
                            <div key={c.id} className="space-y-2">
                                <Label className="text-base font-medium">{c.label}</Label>
                                <p className="text-sm text-muted-foreground">{c.description}</p>
                                <StarRating
                                    value={criteria[c.id] || 0}
                                    onChange={(val) => setCriteria(prev => ({ ...prev, [c.id]: val }))}
                                    disabled={submitting}
                                />
                            </div>
                        ))}

                        {/* Overall Score Preview */}
                        {overallScore && (
                            <div className="p-4 rounded-lg bg-muted/50 flex items-center justify-between">
                                <span className="font-medium">Note globale</span>
                                <div className="flex items-center gap-2">
                                    <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                                    <span className="text-2xl font-bold">{overallScore}</span>
                                    <span className="text-muted-foreground">/5</span>
                                </div>
                            </div>
                        )}

                        {/* Comment */}
                        <div className="space-y-2">
                            <Label className="text-base font-medium">
                                Commentaire (optionnel)
                            </Label>
                            <Textarea
                                placeholder="Partagez votre expérience en quelques mots..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                disabled={submitting}
                                maxLength={800}
                                rows={4}
                            />
                            <p className="text-xs text-muted-foreground text-right">
                                {comment.length} / 800 caractères
                            </p>
                        </div>

                        {/* Info box */}
                        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 flex gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-blue-900">Comment fonctionne la notation ?</p>
                                <ul className="mt-1 text-blue-700 space-y-1">
                                    <li>• Notes ≥ 3.5 : publication immédiate</li>
                                    <li>• Notes &lt; 3.5 : l'agent peut répondre avant publication</li>
                                    <li>• Vos coordonnées ne sont jamais visibles publiquement</li>
                                </ul>
                            </div>
                        </div>

                        {/* Submit */}
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting || !overallScore}
                            className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white"
                            size="lg"
                        >
                            {submitting ? (
                                <>
                                    <div className="spinner w-4 h-4 mr-2"></div>
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Envoyer mon avis
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
